import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Download, Loader2, Eye, EyeOff, Save, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API } from "../../lib/api";
import { useTitle } from "../../App";

const STATUSES = ["New", "Contacted", "Qualified", "Meeting", "Proposal", "Won", "Lost"];
const STATUS_COLORS = {
  New: "bg-violet/15 text-violet", Contacted: "bg-sky-400/15 text-sky-300", Qualified: "bg-teal/15 text-teal",
  Meeting: "bg-amber-400/15 text-amber-300", Proposal: "bg-orange-400/15 text-orange-300",
  Won: "bg-emerald-400/15 text-emerald-300", Lost: "bg-slate-500/15 text-slate-400",
};
const CATS = ["grow-my-brand", "digital-product", "travel-experience", "cafe-updates", "partnership", "general", "help-support"];

const inputCls = "w-full rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-mist placeholder:text-slate-500 focus:border-violet/60 focus:outline-none";

function Stats({ stats }) {
  if (!stats) return null;
  const cards = [
    ["Total enquiries", stats.total_leads],
    ["Last 7 days", stats.last_7_days],
    ["New", stats.by_status?.New || 0],
    ["Won", stats.by_status?.Won || 0],
    ["WhatsApp subscribers", stats.subscribers || 0],
    ["Draft ventures", stats.draft_ventures],
  ];
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-6" data-testid="admin-stats">
      {cards.map(([label, val]) => (
        <div key={label} className="rounded-xl border border-white/[0.07] bg-deep p-5">
          <p className="font-display text-3xl font-bold text-mist">{val}</p>
          <p className="mt-1 text-xs text-slate-400">{label}</p>
        </div>
      ))}
    </div>
  );
}

function LeadsTab() {
  const [filters, setFilters] = useState({ status: "", category: "", q: "", date_from: "", date_to: "" });
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [note, setNote] = useState("");
  const [followUp, setFollowUp] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const { data } = await api.get("/admin/leads", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const patch = async (id, body) => {
    try {
      await api.patch(`/admin/leads/${id}`, body);
      toast.success("Lead updated");
      setNote("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const exportCsv = async () => {
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const res = await fetch(`${API}/admin/leads-export?${new URLSearchParams(params)}`, { credentials: "include" });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "ekavrix-leads.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${data.total} lead(s)`);
    } catch (e) {
      toast.error("Export failed. Please try again.");
    }
  };

  const setF = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div>
      <div className="mb-6 grid gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="leads-filters">
        <input data-testid="filter-search" value={filters.q} onChange={setF("q")} placeholder="Search name, email, company" className={inputCls} />
        <select data-testid="filter-status" value={filters.status} onChange={setF("status")} className={inputCls}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select data-testid="filter-category" value={filters.category} onChange={setF("category")} className={inputCls}>
          <option value="">All categories</option>
          {CATS.map((c) => <option key={c}>{c}</option>)}
        </select>
        <input data-testid="filter-from" type="date" value={filters.date_from} onChange={setF("date_from")} className={inputCls} />
        <input data-testid="filter-to" type="date" value={filters.date_to} onChange={setF("date_to")} className={inputCls} />
        <button data-testid="export-csv" onClick={exportCsv} className="flex items-center justify-center gap-2 rounded-lg bg-teal px-4 py-2 font-display text-sm font-semibold text-midnight transition-colors hover:bg-teal-hover">
          <Download size={15} /> Export CSV
        </button>
      </div>

      {loading ? (
        <p className="py-16 text-center text-slate-500"><Loader2 className="mx-auto animate-spin" /></p>
      ) : data.items.length === 0 ? (
        <div data-testid="leads-empty" className="rounded-xl border border-dashed border-white/10 py-16 text-center">
          <p className="font-display text-lg text-slate-300">No enquiries match</p>
          <p className="mt-1 text-sm text-slate-500">New form submissions will appear here automatically.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="leads-list">
          {data.items.map((l) => (
            <div key={l.id} className="rounded-xl border border-white/[0.07] bg-deep">
              <button data-testid={`lead-row-${l.id.slice(0, 8)}`} onClick={() => { setOpen(open === l.id ? null : l.id); setNote(""); setFollowUp(l.follow_up || ""); }}
                className="grid w-full items-center gap-3 px-5 py-4 text-left sm:grid-cols-12">
                <div className="sm:col-span-3">
                  <p className="font-display text-sm font-semibold text-mist">{l.name}</p>
                  <p className="text-xs text-slate-500">{l.email}</p>
                </div>
                <p className="text-xs text-slate-400 sm:col-span-2">{l.category}{l.venture ? ` · ${l.venture}` : ""}</p>
                <p className="line-clamp-1 text-xs text-slate-500 sm:col-span-4">{l.message}</p>
                <p className="font-mono text-[10px] text-slate-500 sm:col-span-2">{new Date(l.created_at).toLocaleDateString()}</p>
                <span className={`w-fit rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest sm:col-span-1 ${STATUS_COLORS[l.status] || ""}`}>{l.status}</span>
              </button>
              {open === l.id && (
                <div className="border-t border-white/[0.07] px-5 py-5" data-testid={`lead-detail-${l.id.slice(0, 8)}`}>
                  <p className="text-sm leading-relaxed text-slate-300">{l.message}</p>
                  <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500">
                    {l.phone && <span>Phone: {l.phone}</span>}
                    {l.city && <span>City: {l.city}</span>}
                    {l.company && <span>Company: {l.company}</span>}
                    {l.website && <span>Website: {l.website}</span>}
                    {l.budget_range && <span>Budget: {l.budget_range}</span>}
                    {l.routed_to && <span>Routed to: {l.routed_to}</span>}
                    {l.source_page && <span>From: {l.source_page}</span>}
                    {l.utm_campaign && <span>Campaign: {l.utm_campaign}</span>}
                    <span>WhatsApp opt-in: {l.whatsapp_consent ? "yes" : "no"}</span>
                    <span>Promo consent: {l.promo_consent ? "yes" : "no"}</span>
                  </div>
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    {STATUSES.map((s) => (
                      <button key={s} data-testid={`status-${s.toLowerCase()}-${l.id.slice(0, 8)}`} onClick={() => patch(l.id, { status: s })}
                        className={`rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-widest transition-colors ${l.status === s ? "border-violet bg-violet/20 text-white" : "border-white/10 text-slate-400 hover:border-violet/40"}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                  {l.notes?.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {l.notes.map((n, i) => (
                        <p key={i} className="rounded-lg bg-white/[0.03] px-3 py-2 text-xs text-slate-400">
                          {n.text} <span className="text-slate-600">— {new Date(n.at).toLocaleString()}</span>
                        </p>
                      ))}
                    </div>
                  )}
                  <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_200px_auto]">
                    <input data-testid={`note-input-${l.id.slice(0, 8)}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a private note..." className={inputCls} />
                    <input data-testid={`followup-${l.id.slice(0, 8)}`} type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)} className={inputCls} />
                    <button data-testid={`note-save-${l.id.slice(0, 8)}`} onClick={() => patch(l.id, { note: note || undefined, follow_up: followUp })}
                      className="flex items-center justify-center gap-2 rounded-lg bg-violet px-4 py-2 font-display text-sm font-semibold text-white transition-colors hover:bg-violet-hover">
                      <Save size={14} /> Save
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BroadcastTab() {
  const [subs, setSubs] = useState({ items: [], counts: {}, segments: [] });
  const [broadcasts, setBroadcasts] = useState({ items: [], provider_active: false });
  const [form, setForm] = useState({ name: "", segment: "", message: "", scheduled_at: "" });
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/subscribers").then((r) => setSubs(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/broadcasts").then((r) => setBroadcasts(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (creating) return;
    setCreating(true);
    try {
      await api.post("/admin/broadcasts", { ...form, scheduled_at: form.scheduled_at || null });
      toast.success("Broadcast saved as draft");
      setForm({ name: "", segment: "", message: "", scheduled_at: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setCreating(false); }
  };

  const send = async (id) => {
    try {
      const { data } = await api.post(`/admin/broadcasts/${id}/send`);
      if (data.ok) toast.success(`Broadcast sent: ${data.sent} delivered, ${data.failed} failed`);
      else toast.info(`Queued: ${data.detail}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/broadcasts/${id}`);
      toast.success("Draft deleted");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleOptOut = async (sub) => {
    try {
      await api.patch(`/admin/subscribers/${sub.id}`, { opted_out: !sub.opted_out });
      toast.success(sub.opted_out ? "Subscriber re-activated" : "Subscriber opted out");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="grid gap-8 lg:grid-cols-12" data-testid="broadcast-tab">
      <div className="lg:col-span-5">
        <div className="rounded-xl border border-white/[0.07] bg-deep p-6">
          <h3 className="font-display text-base font-semibold">Create broadcast</h3>
          {!broadcasts.provider_active && (
            <p className="mt-2 rounded-lg border border-amber-400/20 bg-amber-400/[0.06] px-3 py-2 text-xs leading-relaxed text-amber-300">
              WhatsApp provider not connected. Campaigns can be created and queued now; messages send once Meta WhatsApp Cloud API, Twilio, Gupshup or MSG91 credentials are added by the developer. Only opted-in subscribers are ever included.
            </p>
          )}
          <form onSubmit={create} className="mt-4 space-y-3">
            <input data-testid="broadcast-name" required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Campaign name" className={inputCls} />
            <select data-testid="broadcast-segment" required value={form.segment} onChange={(e) => setForm({ ...form, segment: e.target.value })} className={inputCls}>
              <option value="" disabled>Select segment</option>
              {(subs.segments || []).map((s) => (
                <option key={s} value={s}>{s} ({subs.counts?.[s] || 0} opted-in)</option>
              ))}
            </select>
            <textarea data-testid="broadcast-message" required minLength={2} rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Message text..." className={inputCls} />
            <div>
              <label className="mb-1 block text-xs text-slate-400">Schedule (optional)</label>
              <input data-testid="broadcast-schedule" type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} className={inputCls} />
            </div>
            {form.message && (
              <div className="rounded-xl rounded-bl-sm border border-teal/25 bg-teal/[0.06] px-4 py-3" data-testid="broadcast-preview">
                <p className="mb-1 font-mono text-[9px] uppercase tracking-widest text-teal">Preview</p>
                <p className="whitespace-pre-wrap text-sm text-slate-200">{form.message}</p>
              </div>
            )}
            <button data-testid="broadcast-create" type="submit" disabled={creating} className="w-full rounded-lg bg-violet px-4 py-2.5 font-display text-sm font-semibold text-white transition-colors hover:bg-violet-hover disabled:opacity-60">
              {creating ? "Saving..." : "Save Draft Broadcast"}
            </button>
          </form>
        </div>

        <div className="mt-6 rounded-xl border border-white/[0.07] bg-deep p-6">
          <h3 className="font-display text-base font-semibold">Campaigns</h3>
          <div className="mt-4 space-y-3">
            {broadcasts.items.length === 0 && <p className="text-sm text-slate-500">No broadcasts yet.</p>}
            {broadcasts.items.map((b) => (
              <div key={b.id} data-testid={`broadcast-${b.id.slice(0, 8)}`} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-display text-sm font-semibold text-mist">{b.name}</p>
                    <p className="mt-0.5 text-xs text-slate-500">{b.segment} · {b.recipients} recipients{b.sent ? ` · ${b.sent} sent` : ""}{b.failed ? ` · ${b.failed} failed` : ""}</p>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-400">{b.status}{b.scheduled_at ? ` · ${new Date(b.scheduled_at).toLocaleString()}` : ""}</p>
                  </div>
                  <div className="flex gap-2">
                    {b.status !== "Sent" && (
                      <>
                        <button data-testid={`broadcast-send-${b.id.slice(0, 8)}`} onClick={() => send(b.id)} title={broadcasts.provider_active ? "Send now" : "Queue (provider not connected)"}
                          className="flex items-center gap-1.5 rounded-lg bg-teal px-3 py-1.5 text-xs font-semibold text-midnight transition-colors hover:bg-teal-hover">
                          <Send size={12} /> Send
                        </button>
                        <button data-testid={`broadcast-delete-${b.id.slice(0, 8)}`} onClick={() => remove(b.id)}
                          className="rounded-lg border border-white/10 px-2.5 py-1.5 text-slate-400 transition-colors hover:border-red-400/40 hover:text-red-300">
                          <Trash2 size={13} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <p className="mt-2 line-clamp-2 text-xs text-slate-500">{b.message}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="lg:col-span-7">
        <div className="rounded-xl border border-white/[0.07] bg-deep p-6">
          <h3 className="font-display text-base font-semibold">Opted-in subscribers</h3>
          <div className="mt-4 flex flex-wrap gap-2">
            {(subs.segments || []).map((s) => (
              <span key={s} className="rounded-full border border-white/10 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-slate-400">
                {s}: <span className="text-teal">{subs.counts?.[s] || 0}</span>
              </span>
            ))}
          </div>
          <div className="mt-5 space-y-2" data-testid="subscriber-list">
            {subs.items.length === 0 && (
              <p className="py-8 text-center text-sm text-slate-500">No opted-in subscribers yet. They appear here when visitors tick WhatsApp updates on a form.</p>
            )}
            {subs.items.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-white/[0.06] bg-white/[0.02] px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-mist">{s.name || s.phone}</p>
                  <p className="text-xs text-slate-500">{s.phone} · {s.segment} · opted in {s.consent_date ? new Date(s.consent_date).toLocaleDateString() : ""} via {s.consent_source}</p>
                </div>
                <button data-testid={`optout-${s.id.slice(0, 8)}`} onClick={() => toggleOptOut(s)}
                  className={`rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-widest transition-colors ${s.opted_out ? "border-red-400/40 text-red-300" : "border-teal/40 text-teal hover:bg-teal/10"}`}>
                  {s.opted_out ? "Opted out" : "Active"}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ContentTab() {
  const [ventures, setVentures] = useState([]);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});

  const load = useCallback(() => {
    api.get("/admin/ventures").then((r) => setVentures(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (v) => {
    try {
      await api.patch(`/admin/ventures/${v.id}`, { published: !v.published });
      toast.success(v.published ? `${v.name} hidden from public site` : `${v.name} published`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async () => {
    try {
      await api.patch(`/admin/ventures/${edit.id}`, {
        name: form.name, tagline: form.tagline, summary: form.summary,
        availability_note: form.availability_note, cta_label: form.cta_label, order: Number(form.order) || 0,
        ai_tag: form.ai_tag || null, status_label: form.status_label || null,
      });
      toast.success("Venture updated — live on the site immediately");
      setEdit(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-3" data-testid="content-ventures">
      <p className="mb-4 text-sm text-slate-400">Toggle publication or edit public copy. Changes go live immediately; drafts never appear in public navigation, lists or endpoints.</p>
      {ventures.map((v) => (
        <div key={v.id} className="rounded-xl border border-white/[0.07] bg-deep p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-display text-base font-semibold text-mist">{v.name}
                <span className={`ml-3 rounded-full px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${v.published ? "bg-teal/15 text-teal" : "bg-slate-500/15 text-slate-400"}`}>
                  {v.published ? "Published" : "Draft"}
                </span>
                {v.ai_tag && <span className="ml-2 rounded-full bg-violet/15 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-widest text-violet">{v.ai_tag}</span>}
              </p>
              <p className="mt-0.5 text-xs text-slate-500">{v.pillar} · /ventures/{v.slug}</p>
            </div>
            <div className="flex gap-2">
              <button data-testid={`edit-${v.slug}`} onClick={() => { setEdit(edit?.id === v.id ? null : v); setForm(v); }}
                className="rounded-lg border border-white/10 px-4 py-2 text-xs font-medium text-slate-300 transition-colors hover:border-violet/40">Edit</button>
              <button data-testid={`toggle-${v.slug}`} onClick={() => toggle(v)}
                className={`flex items-center gap-2 rounded-lg px-4 py-2 text-xs font-semibold transition-colors ${v.published ? "bg-slate-500/20 text-slate-300 hover:bg-slate-500/30" : "bg-teal text-midnight hover:bg-teal-hover"}`}>
                {v.published ? <><EyeOff size={13} /> Unpublish</> : <><Eye size={13} /> Publish</>}
              </button>
            </div>
          </div>
          {edit?.id === v.id && (
            <div className="mt-5 grid gap-3 border-t border-white/[0.07] pt-5" data-testid={`edit-form-${v.slug}`}>
              <div className="grid gap-3 sm:grid-cols-2">
                <input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className={inputCls} />
                <input value={form.tagline || ""} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="Tagline" className={inputCls} />
              </div>
              <textarea value={form.summary || ""} onChange={(e) => setForm({ ...form, summary: e.target.value })} rows={3} placeholder="Summary" className={inputCls} />
              <div className="grid gap-3 sm:grid-cols-3">
                <input value={form.cta_label || ""} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} placeholder="CTA label" className={inputCls} />
                <input value={form.availability_note || ""} onChange={(e) => setForm({ ...form, availability_note: e.target.value })} placeholder="Availability note" className={inputCls} />
                <input value={form.order ?? ""} type="number" onChange={(e) => setForm({ ...form, order: e.target.value })} placeholder="Display order" className={inputCls} />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <input data-testid={`ai-tag-${v.slug}`} value={form.ai_tag || ""} onChange={(e) => setForm({ ...form, ai_tag: e.target.value })} placeholder='AI label (e.g. "AI Powered") — only if genuine' className={inputCls} />
                <input value={form.status_label || ""} onChange={(e) => setForm({ ...form, status_label: e.target.value })} placeholder='Status label (e.g. "Coming Soon")' className={inputCls} />
              </div>
              <div className="flex gap-2">
                <button data-testid={`save-${v.slug}`} onClick={save} className="rounded-lg bg-violet px-5 py-2 text-xs font-semibold text-white transition-colors hover:bg-violet-hover">Save changes</button>
                <button onClick={() => setEdit(null)} className="rounded-lg border border-white/10 px-5 py-2 text-xs text-slate-400">Cancel</button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function SolutionsTab() {
  const [solutions, setSolutions] = useState([]);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});

  const load = useCallback(() => {
    api.get("/admin/solutions").then((r) => setSolutions(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEdit = (s) => {
    setEdit(edit?.id === s.id ? null : s);
    setForm({ ...s, approachText: (s.approach || []).join("\n"), deliverablesText: (s.deliverables || []).join("\n") });
  };

  const save = async () => {
    try {
      await api.patch(`/admin/solutions/${edit.id}`, {
        title: form.title, summary: form.summary, audience: form.audience, problem: form.problem,
        approach: form.approachText.split("\n").map((x) => x.trim()).filter(Boolean),
        deliverables: form.deliverablesText.split("\n").map((x) => x.trim()).filter(Boolean),
      });
      toast.success("Solution updated — live on the site immediately");
      setEdit(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggle = async (s) => {
    try {
      await api.patch(`/admin/solutions/${s.id}`, { published: !s.published });
      toast.success(s.published ? `${s.title} hidden from public site` : `${s.title} published`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-3" data-testid="content-solutions">
      <p className="mb-4 text-sm text-slate-400">Edit any of the seven solution pages. Changes go live immediately.</p>
      {solutions.map((s) => (
        <div key={s.id} className="rounded-xl border border-white/[0.07] bg-deep p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-display text-base font-semibold text-mist">{s.title}
                <span className={`ml-3 rounded-full px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${s.published ? "bg-teal/15 text-teal" : "bg-slate-500/15 text-slate-400"}`}>
                  {s.published ? "Published" : "Draft"}
                </span>
              </p>
              <p className="mt-0.5 text-xs text-slate-500">/solutions/{s.slug}</p>
            </div>
            <div className="flex gap-2">
              <button data-testid={`edit-sol-${s.slug}`} onClick={() => openEdit(s)}
                className="rounded-lg border border-white/10 px-4 py-2 text-xs font-medium text-slate-300 transition-colors hover:border-violet/40">Edit</button>
              <button data-testid={`toggle-sol-${s.slug}`} onClick={() => toggle(s)}
                className={`flex items-center gap-2 rounded-lg px-4 py-2 text-xs font-semibold transition-colors ${s.published ? "bg-slate-500/20 text-slate-300 hover:bg-slate-500/30" : "bg-teal text-midnight hover:bg-teal-hover"}`}>
                {s.published ? <><EyeOff size={13} /> Unpublish</> : <><Eye size={13} /> Publish</>}
              </button>
            </div>
          </div>
          {edit?.id === s.id && (
            <div className="mt-5 grid gap-3 border-t border-white/[0.07] pt-5" data-testid={`edit-sol-form-${s.slug}`}>
              <input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" className={inputCls} />
              <textarea value={form.summary || ""} onChange={(e) => setForm({ ...form, summary: e.target.value })} rows={2} placeholder="Summary" className={inputCls} />
              <div className="grid gap-3 sm:grid-cols-2">
                <textarea value={form.audience || ""} onChange={(e) => setForm({ ...form, audience: e.target.value })} rows={3} placeholder="Who this is for" className={inputCls} />
                <textarea value={form.problem || ""} onChange={(e) => setForm({ ...form, problem: e.target.value })} rows={3} placeholder="The problem it solves" className={inputCls} />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs text-slate-400">Approach steps (one per line)</label>
                  <textarea data-testid={`approach-${s.slug}`} value={form.approachText || ""} onChange={(e) => setForm({ ...form, approachText: e.target.value })} rows={5} className={inputCls} />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-slate-400">Deliverables (one per line)</label>
                  <textarea value={form.deliverablesText || ""} onChange={(e) => setForm({ ...form, deliverablesText: e.target.value })} rows={5} className={inputCls} />
                </div>
              </div>
              <div className="flex gap-2">
                <button data-testid={`save-sol-${s.slug}`} onClick={save} className="rounded-lg bg-violet px-5 py-2 text-xs font-semibold text-white transition-colors hover:bg-violet-hover">Save changes</button>
                <button onClick={() => setEdit(null)} className="rounded-lg border border-white/10 px-5 py-2 text-xs text-slate-400">Cancel</button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function SettingsTab() {
  const [cfg, setCfg] = useState(null);
  const [testingEmail, setTestingEmail] = useState(false);
  useEffect(() => { api.get("/admin/config").then((r) => setCfg(r.data)).catch(() => setCfg({})); }, []);
  if (!cfg) return <p className="py-10 text-center text-slate-500"><Loader2 className="mx-auto animate-spin" /></p>;

  const set = (k) => (e) => setCfg({ ...cfg, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = async () => {
    try {
      await api.patch("/admin/config", {
        company_name: cfg.company_name, tagline: cfg.tagline, hero_headline: cfg.hero_headline,
        logo_url: cfg.logo_url,
        hero_subcopy: cfg.hero_subcopy, founder_name: cfg.founder_name, founder_bio: cfg.founder_bio,
        founder_role: cfg.founder_role,
        contact_email: cfg.contact_email, whatsapp_number: cfg.whatsapp_number, legal_name: cfg.legal_name,
        founder_photo: cfg.founder_photo, founder_photo_about: cfg.founder_photo_about,
        registration_details: cfg.registration_details, legal_confirmed: !!cfg.legal_confirmed,
        owner_notification_email: cfg.owner_notification_email,
      });
      toast.success("Site settings saved — public pages update immediately");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const testEmail = async () => {
    setTestingEmail(true);
    try {
      await api.patch("/admin/config", { owner_notification_email: cfg.owner_notification_email });
      const { data } = await api.post("/admin/test-email");
      toast.success(`Test email sent to ${data.sent_to}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setTestingEmail(false); }
  };

  return (
    <div className="max-w-2xl space-y-4" data-testid="settings-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <div><label className="mb-1 block text-xs text-slate-400">Company name</label><input value={cfg.company_name || ""} onChange={set("company_name")} className={inputCls} /></div>
        <div><label className="mb-1 block text-xs text-slate-400">Public business email</label><input value={cfg.contact_email || ""} onChange={set("contact_email")} className={inputCls} /></div>
      </div>
      <div><label className="mb-1 block text-xs text-slate-400">Hero headline (one line per row)</label><textarea rows={2} value={cfg.hero_headline || ""} onChange={set("hero_headline")} className={inputCls} /></div>
      <div><label className="mb-1 block text-xs text-slate-400">Hero supporting copy</label><textarea rows={3} value={cfg.hero_subcopy || ""} onChange={set("hero_subcopy")} className={inputCls} /></div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div><label className="mb-1 block text-xs text-slate-400">Founder name</label><input value={cfg.founder_name || ""} onChange={set("founder_name")} className={inputCls} /></div>
        <div><label className="mb-1 block text-xs text-slate-400">Founder role label</label><input value={cfg.founder_role || ""} onChange={set("founder_role")} className={inputCls} /></div>
      </div>
      <div><label className="mb-1 block text-xs text-slate-400">Founder bio</label><textarea rows={4} value={cfg.founder_bio || ""} onChange={set("founder_bio")} className={inputCls} /></div>
      <div><label className="mb-1 block text-xs text-slate-400">Founder photo URL (homepage leadership section)</label><input data-testid="founder-photo" value={cfg.founder_photo || ""} onChange={set("founder_photo")} placeholder="https://..." className={inputCls} /></div>
      <div><label className="mb-1 block text-xs text-slate-400">Founder photo URL — About page (optional; falls back to homepage photo)</label><input data-testid="founder-photo-about" value={cfg.founder_photo_about || ""} onChange={set("founder_photo_about")} placeholder="https://..." className={inputCls} /></div>
      <div className="rounded-xl border border-white/[0.07] bg-deep p-5">
        <p className="font-display text-sm font-semibold text-mist">Brand logo</p>
        <p className="mt-1 text-xs text-slate-500">Paste your official logo image URL. It replaces the text wordmark in the header and footer. Transparent PNG or SVG works best.</p>
        <input data-testid="logo-url" value={cfg.logo_url || ""} onChange={set("logo_url")} placeholder="https://... (not set — text wordmark shown)" className={`${inputCls} mt-4`} />
      </div>
      <div className="rounded-xl border border-white/[0.07] bg-deep p-5">
        <p className="font-display text-sm font-semibold text-mist">WhatsApp Business</p>
        <p className="mt-1 text-xs text-slate-500">Country code + number, digits only (e.g. 919876543210). The floating WhatsApp button appears site-wide only while a number is set.</p>
        <input data-testid="whatsapp-number" value={cfg.whatsapp_number || ""} onChange={set("whatsapp_number")} placeholder="Not set — WhatsApp buttons hidden" className={`${inputCls} mt-4`} />
      </div>
      <div className="rounded-xl border border-white/[0.07] bg-deep p-5">
        <p className="font-display text-sm font-semibold text-mist">Enquiry notifications</p>
        <p className="mt-1 text-xs text-slate-500">Every new enquiry is emailed to this address. The database record is always saved first, so an email failure never loses a lead.</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto]">
          <input data-testid="owner-notify-email" type="email" value={cfg.owner_notification_email || ""} onChange={set("owner_notification_email")} placeholder="you@yourdomain.com" className={inputCls} />
          <button data-testid="test-email-btn" onClick={testEmail} disabled={testingEmail || !cfg.owner_notification_email}
            className="rounded-lg border border-teal/40 px-4 py-2 text-xs font-semibold text-teal transition-colors hover:bg-teal/10 disabled:opacity-50">
            {testingEmail ? "Sending..." : "Send test email"}
          </button>
        </div>
      </div>
      <div className="rounded-xl border border-white/[0.07] bg-deep p-5">
        <p className="font-display text-sm font-semibold text-mist">Legal identity</p>
        <p className="mt-1 text-xs text-slate-500">The legal name and registration details are only shown publicly when you confirm them below.</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <input value={cfg.legal_name || ""} onChange={set("legal_name")} placeholder="Legal name" className={inputCls} />
          <input value={cfg.registration_details || ""} onChange={set("registration_details")} placeholder="Registration details (CIN etc.)" className={inputCls} />
        </div>
        <label className="mt-4 flex items-center gap-3 text-sm text-slate-300">
          <input data-testid="legal-confirmed" type="checkbox" checked={!!cfg.legal_confirmed} onChange={set("legal_confirmed")} className="accent-violet" />
          I confirm these legal details are accurate and may be displayed publicly
        </label>
      </div>
      <button data-testid="settings-save" onClick={save} className="rounded-lg bg-violet px-6 py-2.5 font-display text-sm font-semibold text-white transition-colors hover:bg-violet-hover">Save Settings</button>
    </div>
  );
}

export default function AdminDashboard() {
  useTitle("Owner Dashboard");
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("leads");
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (user) api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [user, tab]);

  if (user === undefined) return <div className="flex min-h-screen items-center justify-center bg-midnight"><Loader2 className="animate-spin text-violet" /></div>;
  if (user === null) { navigate("/admin/login"); return null; }

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-midnight text-mist">
      <header className="border-b border-white/[0.07] bg-deep/50">
        <div className="container-ek flex h-16 items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-violet to-teal font-display text-sm font-bold text-white">E</span>
            <span className="font-display text-lg font-bold">Ekavrix <span className="text-slate-500">/ Owner</span></span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" className="text-xs text-slate-400 transition-colors hover:text-mist">View site</a>
            <span className="hidden text-xs text-slate-500 sm:block">{user.email}</span>
            <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-2 rounded-lg border border-white/10 px-3 py-1.5 text-xs text-slate-300 transition-colors hover:border-red-400/40 hover:text-red-300">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>
      <main className="container-ek py-10">
        <Stats stats={stats} />
        <div className="mt-8 flex gap-2 overflow-x-auto border-b border-white/[0.07]" role="tablist">
          {[["leads", "Enquiries"], ["broadcast", "WhatsApp Broadcast"], ["content", "Ventures"], ["solutions", "Solutions"], ["settings", "Site Settings"]].map(([k, label]) => (
            <button key={k} data-testid={`tab-${k}`} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
              className={`whitespace-nowrap px-5 py-3 font-display text-sm font-medium transition-colors ${tab === k ? "border-b-2 border-violet text-white" : "text-slate-400 hover:text-slate-200"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="py-8">
          {tab === "leads" && <LeadsTab />}
          {tab === "broadcast" && <BroadcastTab />}
          {tab === "content" && <ContentTab />}
          {tab === "solutions" && <SolutionsTab />}
          {tab === "settings" && <SettingsTab />}
        </div>
      </main>
    </div>
  );
}
